// =====================================================
// DRIVER TRACKER LOCAL STORAGE HELPERS
// =====================================================

const getDriverTrackerStorageKey = (orderId) => {
  return `driverTracker_${String(orderId)}`;
};

const getCachedDriverTracker = (orderId) => {
  try {
    if (typeof window === "undefined") {
      return null;
    }

    const key = getDriverTrackerStorageKey(orderId);

    const cachedData = localStorage.getItem(key);

    if (!cachedData) {
      return null;
    }

    const parsedData = JSON.parse(cachedData);

    console.log("DRIVER TRACKER CACHE FOUND:", parsedData);

    return parsedData;
  } catch (error) {
    console.error("DRIVER TRACKER CACHE READ ERROR:", error);

    return null;
  }
};

// =====================================================
// NORMALIZE TRACKER RESPONSE
//
// OLD API:
// route: []
// polyLine: ""
//
// NEW API:
// tracker_route: "{\"routeCoordinates\":[],\"polyLine\":\"\"}"
// =====================================================

const normalizeDriverTrackerResponse = (responseData) => {
  if (
    !responseData ||
    !Array.isArray(responseData?.data) ||
    responseData.data.length === 0
  ) {
    return responseData;
  }

  const tracker = responseData.data[0];

  if (!tracker) {
    return responseData;
  }

  let parsedTrackerRoute = null;

  // =====================================================
  // PARSE tracker_route
  // =====================================================

  if (tracker?.tracker_route) {
    try {
      parsedTrackerRoute =
        typeof tracker.tracker_route === "string"
          ? JSON.parse(tracker.tracker_route)
          : tracker.tracker_route;

      console.log("PARSED TRACKER ROUTE:", parsedTrackerRoute);
    } catch (error) {
      console.error("TRACKER ROUTE JSON PARSE ERROR:", error);
    }
  }

  // =====================================================
  // ROUTE
  // =====================================================

  let normalizedRoute = [];

  // Old API route
  if (Array.isArray(tracker?.route) && tracker.route.length > 1) {
    normalizedRoute = tracker.route
      .map((item) => ({
        lat: Number(item?.lat ?? item?.Lat),
        lng: Number(item?.lng ?? item?.Lng),
      }))
      .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
  }

  // New API tracker_route.routeCoordinates
  else if (
    Array.isArray(parsedTrackerRoute?.routeCoordinates) &&
    parsedTrackerRoute.routeCoordinates.length > 1
  ) {
    normalizedRoute = parsedTrackerRoute.routeCoordinates
      .map((item) => ({
        lat: Number(item?.Lat ?? item?.lat),
        lng: Number(item?.Lng ?? item?.lng),
      }))
      .filter((item) => Number.isFinite(item.lat) && Number.isFinite(item.lng));
  }

  // =====================================================
  // POLYLINE
  // =====================================================

  const normalizedPolyline =
    typeof tracker?.polyLine === "string" && tracker.polyLine.trim() !== ""
      ? tracker.polyLine
      : typeof parsedTrackerRoute?.polyLine === "string" &&
          parsedTrackerRoute.polyLine.trim() !== ""
        ? parsedTrackerRoute.polyLine
        : "";

  // =====================================================
  // FINAL TRACKER
  // =====================================================

  const finalTracker = {
    ...tracker,

    route: normalizedRoute,

    polyLine: normalizedPolyline,
  };

  const finalResponse = {
    ...responseData,

    data: [finalTracker, ...responseData.data.slice(1)],
  };

  console.log("NORMALIZED DRIVER TRACKER RESPONSE:", finalResponse);

  console.log("NORMALIZED ROUTE:", normalizedRoute);

  console.log("NORMALIZED POLYLINE:", normalizedPolyline);

  return finalResponse;
};

// =====================================================
// SAVE DRIVER TRACKER CACHE
// =====================================================

const saveDriverTrackerToCache = (orderId, data) => {
  try {
    if (typeof window === "undefined") {
      return;
    }

    const trackerData = data?.data?.[0];

    if (!trackerData) {
      return;
    }

    const hasRoute =
      Array.isArray(trackerData?.route) && trackerData.route.length > 1;

    const hasPolyline =
      typeof trackerData?.polyLine === "string" &&
      trackerData.polyLine.trim() !== "";

    if (!hasRoute && !hasPolyline) {
      return;
    }

    const key = getDriverTrackerStorageKey(orderId);

    localStorage.setItem(key, JSON.stringify(data));

    console.log("DRIVER TRACKER SAVED IN LOCAL STORAGE:", data);
  } catch (error) {
    console.error("DRIVER TRACKER CACHE SAVE ERROR:", error);
  }
};

// =====================================================
// DRIVER TRACKER API
// =====================================================

export const getDriverTracker = async (packkyApi, orderId) => {
  try {
    if (!packkyApi) {
      throw new Error("Packky API instance is required");
    }

    if (!orderId) {
      throw new Error("Order ID is required");
    }

    const payload = {
      orderid: String(orderId),
    };

    console.log("DRIVER TRACKER PAYLOAD:", payload);

    // =====================================================
    // OLD CACHED ROUTE
    // =====================================================

    const cachedResponse = getCachedDriverTracker(orderId);

    const cachedTracker = cachedResponse?.data?.[0] || null;

    // =====================================================
    // API CALL
    // =====================================================

    const response = await packkyApi.post(
      "/api/DriverTracker/v1.0/getDriverTrackerRoute",
      payload,
    );

    console.log("DRIVER TRACKER API RESPONSE:", response?.data);

    // =====================================================
    // API VALIDATION
    // =====================================================

    if (response?.data?.responseCode !== "00") {
      throw new Error(
        response?.data?.responseMessage || "Unable to fetch driver tracking",
      );
    }

    // =====================================================
    // NORMALIZE NEW API RESPONSE
    // =====================================================

    const apiResponse = normalizeDriverTrackerResponse(response.data);

    const apiTracker = apiResponse?.data?.[0] || null;

    // =====================================================
    // CURRENT API ROUTE CHECK
    // =====================================================

    const apiHasRoute =
      Array.isArray(apiTracker?.route) && apiTracker.route.length > 1;

    const apiHasPolyline =
      typeof apiTracker?.polyLine === "string" &&
      apiTracker.polyLine.trim() !== "";

    // =====================================================
    // CACHE ROUTE CHECK
    // =====================================================

    const cacheHasRoute =
      Array.isArray(cachedTracker?.route) && cachedTracker.route.length > 1;

    const cacheHasPolyline =
      typeof cachedTracker?.polyLine === "string" &&
      cachedTracker.polyLine.trim() !== "";

    // =====================================================
    // CASE 1
    // API ROUTE AVAILABLE
    // =====================================================

    if (apiHasRoute || apiHasPolyline) {
      console.log("DRIVER TRACKER: ROUTE RECEIVED FROM API");

      saveDriverTrackerToCache(orderId, apiResponse);

      return apiResponse;
    }

    // =====================================================
    // CASE 2
    // API EMPTY BUT CACHE AVAILABLE
    // =====================================================

    if (cacheHasRoute || cacheHasPolyline) {
      console.log("DRIVER TRACKER: USING ROUTE FROM LOCAL STORAGE");

      const finalTracker = {
        ...cachedTracker,
        ...apiTracker,

        polyLine: apiHasPolyline
          ? apiTracker?.polyLine
          : cachedTracker?.polyLine || "",

        route: apiHasRoute ? apiTracker?.route : cachedTracker?.route || [],
      };

      const finalResponse = {
        ...apiResponse,

        data: [
          finalTracker,

          ...(Array.isArray(apiResponse?.data)
            ? apiResponse.data.slice(1)
            : []),
        ],
      };

      console.log("DRIVER TRACKER FINAL RESPONSE WITH CACHE:", finalResponse);

      return finalResponse;
    }

    // =====================================================
    // CASE 3
    // NO ROUTE
    // =====================================================

    console.log("DRIVER TRACKER: NO ROUTE AVAILABLE");

    return apiResponse;
  } catch (error) {
    console.error(
      "DRIVER TRACKER API ERROR:",
      error?.response?.data || error?.message || error,
    );

    // =====================================================
    // API FAIL -> CACHE
    // =====================================================

    const cachedResponse = getCachedDriverTracker(orderId);

    const cachedTracker = cachedResponse?.data?.[0];

    const hasCachedRoute =
      Array.isArray(cachedTracker?.route) && cachedTracker.route.length > 1;

    const hasCachedPolyline =
      typeof cachedTracker?.polyLine === "string" &&
      cachedTracker.polyLine.trim() !== "";

    if (cachedResponse && (hasCachedRoute || hasCachedPolyline)) {
      console.log("API FAILED → USING CACHED DRIVER ROUTE");

      return cachedResponse;
    }

    throw error;
  }
};

export default getDriverTracker;
